import { FormikContextType } from 'formik'
import { FC, useMemo } from 'react'

import { useTranslation } from '../../../../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../../../../../brunswick/containers/embroidery'
import { IconFormValues } from '../../../../../../../../brunswick/containers/embroidery/types'
import { EmbroideryIconAsset, EmbroideryVariant } from '../../../../../../../../types/graphql'
import { EmbroideryIconGroup } from '../../../../hooks/useFetchEmbroideryIconGroups'
import { IconGroupSection } from '../../../IconGroup/IconGroupSection'
import { EmbroideryOptionWrapper, ErrorMessage } from '../../shared-form-styles'

import { IconName, IconSelectorLabel, IconSelectorLabelNoGroups, LabelWrapper } from './styles'

interface Props {
  labelId: string
  form: Pick<FormikContextType<IconFormValues>, 'setFieldValue' | 'touched' | 'errors' | 'values'>
  embroideryOptionVariants: EmbroideryVariant[]
  embroideryIconGroups: EmbroideryIconGroup[]
}

export const IconSelectorForm: FC<Props> = ({
  form,
  embroideryOptionVariants,
  labelId,
  embroideryIconGroups,
}) => {
  const { productColorHex } = EmbroideryService.useContainer()

  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()

  const embroideryIconGroupsInactive: boolean = embroideryIconGroups.length === 0
  const iconImages: EmbroideryIconAsset[] = useMemo(() => {
    return embroideryOptionVariants.flatMap(o => o.images).filter(o => !!o.designFile)
  }, [embroideryOptionVariants])

  const iconImagesGrouped = iconImages.reduce<{ [index: string]: EmbroideryIconAsset[] }>(
    (acc, curr) => {
      const currentGroupName = curr.embroideryIconGroup ?? embroideryIconGroups[0]?.name
      if (currentGroupName) {
        acc[currentGroupName] ||= []
        acc[currentGroupName]?.push(curr)
      }

      return acc
    },
    {}
  )

  const handleSelectIcon = (iconImage: EmbroideryIconAsset) => {
    if (form.values.iconImage && form.values.iconImage === iconImage) {
      form.setFieldValue('iconImage', undefined)
      form.touched.iconImage = true
    } else {
      form.setFieldValue('iconImage', iconImage)
    }
  }
  const embroideryIconGroupsView = embroideryIconGroups.map(group => {
    return (
      <IconGroupSection
        key={`icon-group-${group.name}`}
        name={group.name}
        displayName={group.displayName}
        images={iconImagesGrouped[group.name] ?? []}
        maxIconsPerRowMobile={group.maxIconsPerRowMobile}
        onSelectIcon={handleSelectIcon}
        productColorHex={productColorHex}
        selectedImage={form.values.iconImage}
      />
    )
  })

  if (embroideryIconGroupsInactive) {
    // If there are no published Embroidery Icon Groups in Contentful,
    // group all icons under one section without a group label.
    embroideryIconGroupsView.push(
      <IconGroupSection
        key={`icon-group-default`}
        name={''}
        displayName={''}
        images={iconImages}
        onSelectIcon={handleSelectIcon}
        productColorHex={productColorHex}
        selectedImage={form.values.iconImage}
      />
    )
  }

  const errorMessage = form.touched.iconImage ? form.errors.iconImage : undefined
  const selectedImage = form.values.iconImage

  return (
    <EmbroideryOptionWrapper>
      <LabelWrapper>
        {embroideryIconGroupsInactive ? (
          <IconSelectorLabelNoGroups id={labelId}>
            {fixtures.iconOption.iconSelectorLabel}
          </IconSelectorLabelNoGroups>
        ) : (
          <IconSelectorLabel id={labelId}>
            {fixtures.iconOption.iconSelectorLabel}
          </IconSelectorLabel>
        )}
        {selectedImage && selectedImage.caption && <IconName>{selectedImage.caption}</IconName>}
      </LabelWrapper>

      {embroideryIconGroupsView}

      {errorMessage && (
        <ErrorMessage inset role='alert'>
          {errorMessage}
        </ErrorMessage>
      )}
    </EmbroideryOptionWrapper>
  )
}
